import React, { useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import produce from 'immer';
import clsx from 'clsx';
import { HomeDevice } from '@moderepo/mode-internal-apis';
import { GatewayDefinition, SortOrder } from '@moderepo/mode-apis';
import { HomeDevicesTableDataItem, HomeDevicesTableProps, HomeDevicesTable, PaginationBar, capitalizeAll, capitalizeFirst,
    ITEMS_PER_PAGE_10, useSearchParams, ITEMS_PER_PAGE_LIST, PAGING_MIN_PAGES, BaseListCompField, FluxDSDeviceCompCtrl, GenericMessageBox,
    BaseCompAction } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById, selectLoggedInAgent } from '@moderepo/mode-data-state-ritz';
import { fetchHomeDevices, userAppDataContext, selectHomeDevices } from '@moderepo/mode-data-state-user-app';
import { modeUIContext, selectSlideOutPanelOptions, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useNavigate } from 'react-router-dom';
import { useGetProjectGatewayCatalog, useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime, getDateTimeDisplay } from '../containerUtils';
import { DevicePreviewCompOptions, isDevicePreviewCompOptions, SlideOutPanelCompType, DeviceRegistrationWizardSlideOutCompOptions }
    from '../routes/defaultProjectConfig';
import { RouteHelper } from '../routes/RouteHelper';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { isDeviceRegistrationWizardEnabled } from '../../utils/utils';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';
import { DeviceRegistrationWizardSettings } from '../../utils/ritzUIExtensionUtils';


interface DeviceSearchParams {
    readonly sortField: keyof HomeDevice;
    readonly sortOrder: SortOrder;
}



/**
 * Because this is a container, not a page, it doesn't and shouldn't have access to the route params. Therefore, the page
 * that display this container must provide these information.
 */
interface HomeDevicesContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly userId?: number | undefined;
    readonly homeId: number;
    readonly itemsPerPage?: number;
    readonly gatewaysOnly?: boolean;
}


/**
 * This is a container (SMART COMPONENT) used for displaying a list of user home's devices. Since this is a container, it will have
 * little to no UI. All the UI will be components combined together to build the content.
 */
export const HomeDevicesContainer: React.FC<HomeDevicesContainerProps> = (props: HomeDevicesContainerProps) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState);
    const [selectedDeviceId, setSelectedDeviceId] = useState<number>();
    const { trans } = useRitzTranslation(project);
    const slideOutPanelOptions = selectSlideOutPanelOptions(uiState);
    const deviceController = useMemo(() => {
        return new FluxDSDeviceCompCtrl(appDataDispatch);
    }, [appDataDispatch]);
    const gatewayCatalog = useGetProjectGatewayCatalog(project);
    const navigate = useNavigate();


    // Use the gatewayCatalog and create a map of deviceClasses so that we can easily lookup the map and check if a deviceClass is a gateway
    // e.g.     { agvGateway: true, ruber_3000: true, ...}
    const gatewayClasses = useMemo(() => {
        if (props.gatewaysOnly && gatewayCatalog) {
            return gatewayCatalog.reduce((
                result: {[deviceClass: string]: boolean}, gatewayDef: GatewayDefinition,
            ): {[deviceClass: string]: boolean} => {
                return {
                    ...result,
                    [gatewayDef.deviceClass]: true,
                };
            }, {});
        }
        return {};
    }, [props.gatewaysOnly, gatewayCatalog]);


    /**
     * Because this container can be used to display ALL type of devices or only Gateways therefore the translations will depend on whether we are
     * displaying all or gatewaysOnly. If we are displaying all type of devices then all the text/labels/messages will use the term "device"
     * for devices. If we are showing only gateways then instead of using the term "device", we will show a more specific term, "gateway".
     * This map DOES NOT include all the text we use on this page, only the text that has the word "device" in it.
     */
    const labels = useMemo((): {
        readonly panelTitle: string;
        readonly panelIcon: string;
        readonly deviceClass: string;
        readonly emptyListMessageTitle: string;
    } => {
        if (props.gatewaysOnly) {
            return {
                panelTitle           : capitalizeAll(trans('terms.gateway', { count: 2 })),
                panelIcon            : trans('icons.gateway'),
                deviceClass          : capitalizeFirst(trans('terms.gateway_class')),
                emptyListMessageTitle: trans('generic.no_data.title', { term: capitalizeAll(trans('terms.gateway', { count: 2 })) }),
            };
        }

        return {
            panelTitle           : capitalizeAll(trans('terms.device', { count: 2 })),
            panelIcon            : trans('icons.device'),
            deviceClass          : capitalizeFirst(trans('terms.device_class')),
            emptyListMessageTitle: trans('generic.no_data.title', { term: capitalizeAll(trans('terms.device', { count: 2 })) }),
        };
    }, [props.gatewaysOnly, trans]);



    const {
        searchParams, onItemsPerPageChange, onOtherFieldsChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<DeviceSearchParams>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {
            sortField: 'id',
            sortOrder: SortOrder.ASC,
        },
    });

    const devices = selectHomeDevices(appDataState, props.homeId);



    const sortedDevices = useMemo(() => {
        return deviceController.sortHomeDevices(devices, searchParams.otherFields.sortField, searchParams.otherFields.sortOrder);
    }, [devices, searchParams.otherFields, deviceController]);



    // Get user's devices API call returns everything so we need to filter from the frontend
    const filteredDevices = useMemo(() => {
        if (sortedDevices) {
            // First, filter out the devices that are NOT gateways if props.gatewaysOnly is true
            const includedDevices = props.gatewaysOnly ? sortedDevices.filter((device: HomeDevice): boolean => {
                return gatewayClasses[device.deviceClass];
            }) : sortedDevices;

            // Return the subset of devices based on the current page and page size.
            return deviceController.sliceHomeDevices(
                includedDevices, searchParams.currentPage * searchParams.itemsPerPage, searchParams.itemsPerPage,
            );
        }
        return undefined;
    }, [sortedDevices, gatewayClasses, props.gatewaysOnly, searchParams.currentPage, searchParams.itemsPerPage, deviceController]);



    /**
     * This useEffect used for loading user's devices data on component init
     */
    useEffect(() => {
        if (!devices) {
            appDataDispatch(fetchHomeDevices(props.homeId)).catch(() => {
                // do nothing
            });
        }
    }, [devices, props.homeId, appDataDispatch]);



    /**
     * On home device row selected, go immediately to the target home device detail page.
     */
    const onHomeDeviceSelected = useCallback((device: HomeDevice) => {
        RouteHelper.getInstance(navigate).gotoDevicePage({
            projectId: props.projectId, userId: props.userId, homeId: device.homeId, deviceId: device.id,
        });
    }, [navigate, props.projectId, props.userId]);



    /**
     * On user selected, change the selectedEntity to be the selected user
     * If 'deviceId' object is NOT specified, it will be treated as Creating New device
     */
    const onPreviewHomeDeviceClicked = useCallback((deviceId: number | undefined) => {
        setSelectedDeviceId(deviceId);

        const options: DevicePreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.DEVICE_PREVIEW,
            compProps: {
                data   : { projectId: props.projectId, deviceId, gatewaysOnly: props.gatewaysOnly },
                context: { userId: props.userId, homeId: props.homeId },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, props.gatewaysOnly, props.userId, props.homeId, uiDispatch]);



    // This useEffect is used for checking the URL to see if there is any selectedEntity and if that selected entity is a Device id.
    // if it is, automatically set it as the selectedDeviceId
    useEffect(() => {
        if (isDevicePreviewCompOptions(slideOutPanelOptions) && slideOutPanelOptions.compProps.data?.deviceId !== undefined) {
            setSelectedDeviceId(slideOutPanelOptions.compProps.data.deviceId);
        } else {
            setSelectedDeviceId(undefined);
        }
    }, [slideOutPanelOptions]);



    /**
     * Localize the device data before passing it to the Device Component for display
     */
    const localizeDeviceData = useMemo((): readonly HomeDevicesTableDataItem[] | undefined => {
        if (filteredDevices) {
            return filteredDevices.map((device: HomeDevice): HomeDevicesTableDataItem => {
                const connectivityDate = device.isConnected ? device.lastConnectTime : device.lastDisconnectTime;

                // Find the Gateway from the gatewayCatalog that has the same deviceClass as the device
                const gatewayDefinition = gatewayCatalog?.find((gatewayDef: GatewayDefinition) => {
                    return gatewayDef.deviceClass === device.deviceClass;
                });

                return {
                    actualValue : device,
                    displayValue: {
                        id         : device.id.toString(),
                        // Use the gateway's user friendly name if we have it. If not use deviceClass
                        deviceClass: gatewayDefinition?.name || device.deviceClass,
                        name       : device.name,
                        tag        : device.tag,
                        status     : capitalizeAll(trans(device.isConnected
                            ? 'label.connected'
                            : 'label.disconnected')),
                        connectivityDate: connectivityDate ? getDateTimeAndElapsedTime(
                            connectivityDate, trans, false,
                        ) : capitalizeFirst(trans('label.none')),
                        claimTime: device.claimTime
                            ? getDateTimeDisplay(device.claimTime, trans)
                            : capitalizeAll(trans('label.none')),
                    },
                    selected: selectedDeviceId === device.id,
                };
            });
        }

        return undefined;
    }, [filteredDevices, gatewayCatalog, trans, selectedDeviceId]);



    /**
     * On column clicked, change the sort order/field
     */
    const onSortByField = useCallback((field: BaseListCompField) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {

                if (draft.sortField === field.dataItemProp) {
                    // this column is currently being sorted, change the toggle the sort order instead
                    if (draft.sortOrder === SortOrder.ASC) {
                        draft.sortOrder = SortOrder.DESC;
                    } else {
                        draft.sortOrder = SortOrder.ASC;
                    }
                } else {
                    // This column is not the current column being sorted, change it to this column and
                    draft.sortField = field.dataItemProp;
                }
            }));
        }
    }, [searchParams, onOtherFieldsChange]);


    // Create tableSettings and cache it
    const tableFieldsSettings = useMemo((): HomeDevicesTableProps['fieldsSettings'] => {
        return {
            fields: {
                preview: {
                    label: '',      // The column doesn't need a label so we can leave it blank
                },
                id: {
                    dataItemProp: 'id',
                    label       : capitalizeAll(trans('label.id')),
                    sortable    : devices && devices.length > 1,
                },
                deviceClass: {
                    dataItemProp: 'deviceClass',
                    label       : labels.deviceClass,
                    sortable    : devices && devices.length > 1,
                },
                tag: {
                    dataItemProp: 'tag',
                    label       : capitalizeAll(trans('label.tag')),
                    sortable    : devices && devices.length > 1,
                },
                name: {
                    dataItemProp: 'name',
                    label       : capitalizeAll(trans('label.name')),
                    sortable    : devices && devices.length > 1,
                },
                status: {
                    dataItemProp: 'isConnected',
                    label       : capitalizeAll(trans('label.status')),
                    sortable    : devices && devices.length > 1,
                },
                connectivityDate: {
                    dataItemProp: 'connectivityDate',
                    label       : capitalizeAll(trans('label.connectivity_date')),
                },
                claimTime: {
                    dataItemProp: 'claimTime',
                    label       : capitalizeAll(trans('label.claim_date')),
                    hidden      : true,
                },
            },
            onSortByField,
            currentSortedField: searchParams.otherFields ? searchParams.otherFields.sortField : undefined,
            currentSortedOrder: searchParams.otherFields ? searchParams.otherFields.sortOrder : undefined,
        };
    }, [trans, searchParams.otherFields, onSortByField, devices, labels.deviceClass]);


    // Create PaginationBar and cache it
    const paginationComp = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : devices ? devices.length : undefined,
                hasMoreItems    : devices !== undefined && devices.length > ((searchParams.currentPage + 1) * searchParams.itemsPerPage),
                texts           : {
                    itemsPerPage    : capitalizeAll(trans('label.paging.items_per_page', { item_name: trans('label.item', { count: 2 }) })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [trans, onItemsPerPageChange, onNextPage, onPrevPage, onPage, devices, searchParams]);


    /**
     * Get the "deviceRegistrationWizard" settings from the project's uiSettings if this feature is enabled
     */
    const deviceRegistrationWizardSettings = useMemo(() => {
        const isEnabled = isDeviceRegistrationWizardEnabled(project, loggedInAgent);
        if (isEnabled) {
            const projectUIConfig = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(
                project?.supportToolSettings.projectType,
            )?.getCustomUIConfig(loggedInAgent, project);
            return projectUIConfig?.enabledFeatures?.deviceRegistrationWizard;
        }
        return undefined;
    }, [loggedInAgent, project]);


    /**
     * On Register Device button clicked, open the slide out panel to show the Device Registration Wizard
     */
    const onRegisterDeviceClicked = useCallback((settings: DeviceRegistrationWizardSettings) => {
        const options: DeviceRegistrationWizardSlideOutCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.DEVICE_REGISTRATION_WIZARD,
            compProps: {
                data: {
                    projectId                       : props.projectId,
                    preselectedHomeId               : props.homeId,
                    deviceRegistrationWizardSettings: settings,
                },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.homeId, props.projectId, uiDispatch]);


    /**
     * Create and cache the primary actions to be shown in the DevicesTable
     */
    const actions = useMemo((): HomeDevicesTableProps['customPrimaryActions'] => {

        const result: BaseCompAction[] = [];

        // if the project enabled "deviceRegistrationWizard", add the "Register Device" action
        if (deviceRegistrationWizardSettings) {
            result.push({
                label  : trans('device_registration_wizard.action.register_device.label'),
                variant: 'contained',
                color  : 'primary',
                onClick: () => {
                    onRegisterDeviceClicked(deviceRegistrationWizardSettings);
                },
            });
        }

        // If the project does not have "deviceRegistrationWizard" feature enabled OR if it does AND the "allowAddDeviceWithoutEntity" is enabled
        // then we will add the tradition "Add" device to the list
        if (!deviceRegistrationWizardSettings || deviceRegistrationWizardSettings.allowAddDeviceWithoutEntity) {
            result.push({
                label  : capitalizeAll(trans('label.add')),
                icon   : 'add',
                variant: 'contained',
                color  : 'primary',
                onClick: () => {
                    onPreviewHomeDeviceClicked(undefined);
                },
            });
        }

        return result;
    }, [deviceRegistrationWizardSettings, onPreviewHomeDeviceClicked, onRegisterDeviceClicked, trans]);


    return (
        <>
            {project && (
                <HomeDevicesTable
                    className={clsx(props.className)}
                    listData={localizeDeviceData}
                    title={labels.panelTitle}
                    icon={labels.panelIcon}
                    customPrimaryActions={actions}
                    onListItemSelected={onHomeDeviceSelected}
                    onPreviewItemClicked={(device: HomeDevice) => {
                        onPreviewHomeDeviceClicked(device.id);
                    }}
                    paginationComp={paginationComp}
                    fieldsSettings={tableFieldsSettings}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={labels.emptyListMessageTitle}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={labels.emptyListMessageTitle}
                        />
                    )}
                />
            )}

            {!project && (
                <CircularProgress />
            )}
        </>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('HomeDevicesContainer', HomeDevicesContainer);
